/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {animate, state, style, transition, trigger} from '@angular/animations';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Directive,
  forwardRef,
  Host,
  Input,
  OnChanges,
  OnDestroy,
  Optional,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import {CanDisable, mixinDisabled, UniqueSelectionDispatcher} from '@angular/material/core';
import {Subject} from 'rxjs/Subject';
import {MdAccordion} from './accordion';
import {AccordionItem} from './accordion-item';
/**
 * \@docs-private
 */
export class MdExpansionPanelBase extends AccordionItem {
/**
 * @param {?} accordion
 * @param {?} _changeDetectorRef
 * @param {?} _uniqueSelectionDispatcher
 */
constructor(accordion: MdAccordion,
              _changeDetectorRef: ChangeDetectorRef,
              _uniqueSelectionDispatcher: UniqueSelectionDispatcher) {
    super(accordion, _changeDetectorRef, _uniqueSelectionDispatcher);
  }
}
export const /** @type {?} */ _MdExpansionPanelMixinBase = mixinDisabled(MdExpansionPanelBase);

/** MdExpansionPanel's states. */
export type MdExpansionPanelState = 'expanded' | 'collapsed';
/**
 * Time and timing curve for expansion panel animations.
 */
export const EXPANSION_PANEL_ANIMATION_TIMING = '225ms cubic-bezier(0.4,0.0,0.2,1)';
/**
 * <md-expansion-panel> component.
 * 
 * This component can be used as a single element to show expandable content, or as one of
 * multiple children of an element with the CdkAccordion directive attached.
 * 
 * Please refer to README.md for examples on how to use it.
 */
export class MdExpansionPanel extends _MdExpansionPanelMixinBase
    implements CanDisable, OnChanges, OnDestroy {
/**
 * Whether the toggle indicator should be hidden.
 */
hideToggle: boolean = false;
/**
 * Stream that emits for changes in `\@Input` properties.
 */
_inputChanges = new Subject<SimpleChanges>();
/**
 * @param {?} accordion
 * @param {?} _changeDetectorRef
 * @param {?} _uniqueSelectionDispatcher
 */
constructor(  accordion: MdAccordion,
              _changeDetectorRef: ChangeDetectorRef,
              _uniqueSelectionDispatcher: UniqueSelectionDispatcher) {
    super(accordion, _changeDetectorRef, _uniqueSelectionDispatcher);
    this.accordion = accordion;
  }
/**
 * Whether the expansion indicator should be hidden.
 * @return {?}
 */
_getHideToggle(): boolean {
    if (this.accordion) {
      return this.accordion.hideToggle;
    }
    return this.hideToggle;
  }
/**
 * Determines whether the expansion panel should have spacing between it and its siblings.
 * @return {?}
 */
_hasSpacing(): boolean {
    if (this.accordion) {
      return (this.expanded ? this.accordion.displayMode : this._getExpandedState()) === 'default';
    }
    return false;
  }
/**
 * Gets the expanded state string.
 * @return {?}
 */
_getExpandedState(): MdExpansionPanelState {
    return this.expanded ? 'expanded' : 'collapsed';
  }
/**
 * @param {?} changes
 * @return {?}
 */
ngOnChanges(changes: SimpleChanges) {
    this._inputChanges.next(changes);
  }
/**
 * @return {?}
 */
ngOnDestroy() {
    this._inputChanges.complete();
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  styleUrls: ['./expansion-panel.css'],
  selector: 'md-expansion-panel, mat-expansion-panel',
  templateUrl: './expansion-panel.html',
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  inputs: ['disabled', 'expanded'],
  host: {
    'class': 'mat-expansion-panel',
    '[class.mat-expanded]': 'expanded',
    '[class.mat-expansion-panel-spacing]': '_hasSpacing()',
  },
  providers: [
    {provide: AccordionItem, useExisting: forwardRef(() => MdExpansionPanel)}
  ],
  animations: [
    trigger('bodyExpansion', [
      state('collapsed', style({height: '0px', visibility: 'hidden'})),
      state('expanded', style({height: '*', visibility: 'visible'})),
      transition('expanded <=> collapsed', animate(EXPANSION_PANEL_ANIMATION_TIMING)),
    ]),
  ],
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: MdAccordion, decorators: [{ type: Optional }, { type: Host }, ]},
{type: ChangeDetectorRef, },
{type: UniqueSelectionDispatcher, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'hideToggle': [{ type: Input },],
};
}

function MdExpansionPanel_tsickle_Closure_declarations() {
/** @type {?} */
MdExpansionPanel.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdExpansionPanel.ctorParameters;
/** @type {?} */
MdExpansionPanel.propDecorators;
/**
 * Whether the toggle indicator should be hidden.
 * @type {?}
 */
MdExpansionPanel.prototype.hideToggle;
/**
 * Stream that emits for changes in `\@Input` properties.
 * @type {?}
 */
MdExpansionPanel.prototype._inputChanges;
}

export class MdExpansionPanelActionRow {static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: 'mat-action-row, md-action-row',
  host: {
    class: 'mat-action-row'
  }
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function MdExpansionPanelActionRow_tsickle_Closure_declarations() {
/** @type {?} */
MdExpansionPanelActionRow.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdExpansionPanelActionRow.ctorParameters;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
